import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1771580295464-a1ff43de90ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwc3dpc3MlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2Nzg2MDUxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    API.get("/public/site-content")
      .then(({ data }) => {
        const savedAnnouncement = data.announcement || {};
        const updates = Array.isArray(savedAnnouncement.updates) && savedAnnouncement.updates.length
          ? savedAnnouncement.updates
          : savedAnnouncement.title && savedAnnouncement.message
            ? [{ title: savedAnnouncement.title, message: savedAnnouncement.message }]
            : [];
        const visibleUpdates = updates.filter((update) => update.title && update.message);
        if (savedAnnouncement.active && visibleUpdates.length) {
          setAnnouncement({ updates: visibleUpdates });
        }
      })
      .catch((requestError) => toast.error(fmtErr(requestError)));
  }, []);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await API.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setForgotBusy(false);
    }
  };

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (mode) => {
    if (mode === "register" && !accepted) {
      setError("Please accept the Terms & Conditions and Privacy Policy to create an account.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = mode === "register" ? { name, email, password, terms_accepted: true } : { email, password };
      const r = await API.post(`/auth/${mode}`, payload);
      setUser(r.data);
      toast.success(`Welcome, ${r.data.name || r.data.email}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-8">
          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-primary">ADS Billing</p>
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">GST Accounting for Every Business</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sales, purchases, inventory, banking, GST reports and ledgers — your complete accounting books.
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form
                className="space-y-4 pt-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit("login");
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.in" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                  <Input id="login-password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                {error && <p className="text-sm font-medium text-destructive" data-testid="auth-error">{error}</p>}
                <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
                  {busy ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form
                className="space-y-4 pt-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit("register");
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="reg-name">Full Name</Label>
                  <Input id="reg-name" data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.in" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Password</Label>
                  <Input id="reg-password" data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
                </div>
                {error && <p className="text-sm font-medium text-destructive" data-testid="auth-error">{error}</p>}
                <label className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="register-terms-label">
                  <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="register-terms-checkbox" />
                  <span>
                    I agree to the <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms &amp; Conditions</Link> and{" "}
                    <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
                  </span>
                </label>
                <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-button">
                  {busy ? "Creating…" : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or continue with</span>
            </div>
          </div>

          <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login-button">
            <GoogleLogo size={18} className="mr-2" /> Google
          </Button>

          <Dialog open={forgotOpen} onOpenChange={(v) => { setForgotOpen(v); if (!v) { setForgotDone(false); setForgotEmail(""); } }}>
            <DialogContent className="max-w-sm" data-testid="forgot-password-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Reset your password</DialogTitle>
                <DialogDescription className="sr-only">Request a password reset link by email</DialogDescription>
              </DialogHeader>
              {forgotDone ? (
                <p className="text-sm text-[#008A52]" data-testid="forgot-success-message">
                  If that email is registered, a reset link is on its way. Check your inbox — the link is valid for 15 minutes.
                </p>
              ) : (
                <form className="space-y-4" onSubmit={submitForgot}>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input id="forgot-email" data-testid="forgot-email-input" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@business.in" />
                  </div>
                  <Button type="submit" className="w-full" disabled={forgotBusy} data-testid="forgot-submit-button">
                    {forgotBusy ? "Sending…" : "Send Reset Link"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="absolute inset-0 flex flex-col justify-between p-8 xl:p-12 text-primary-foreground">
          <div className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.22em]">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-base">A</span>
            ADS Billing
          </div>
          {announcement ? (
            <section
              className="max-h-[75vh] max-w-xl space-y-5 overflow-y-auto rounded-2xl border border-white/20 bg-slate-950/55 p-6 shadow-xl backdrop-blur-md xl:p-8"
              aria-live="polite"
              data-testid="login-announcement"
            >
              {announcement.updates.map((update, index) => (
                <article
                  key={`${update.title}-${index}`}
                  className={index ? "border-t border-white/20 pt-5" : ""}
                  data-testid={`login-announcement-item-${index}`}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-200">Update</p>
                  <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight xl:text-3xl">{update.title}</h2>
                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-white/90">{update.message}</p>
                  {update.link_url && (
                    <a
                      href={update.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex text-sm font-semibold text-emerald-200 underline underline-offset-4 hover:text-white"
                      data-testid={`login-announcement-link-${index}`}
                    >
                      {update.link_label || "Open link"}
                    </a>
                  )}
                </article>
              ))}
            </section>
          ) : (
            <div className="pb-2">
              <p className="font-heading text-2xl font-bold tracking-tight">Billing that works for your business.</p>
              <p className="mt-2 max-w-md text-sm leading-6 opacity-90">
                Create GST invoices, manage inventory, and review your business reports in one place.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
